import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { HelpCircle, Mail } from "lucide-react";
import { Link } from "react-router-dom";

export const HelpMenu = () => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="help-menu-btn"
          className="flex items-center gap-1.5 text-[11px] font-mono text-[#A1A1AA] hover:text-white transition-colors border border-[#27272A] hover:border-[#3F3F46] px-2 py-1"
        >
          <HelpCircle size={12} />
          How to use
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[380px] bg-[#0A0A0A] border-[#27272A] text-white p-0"
        data-testid="help-menu-content"
      >
        <div className="px-4 py-3 border-b border-[#27272A]">
          <div className="label-cap">Quick Start</div>
          <p className="text-[11px] text-[#A1A1AA] mt-1 leading-relaxed">
            ROLL.OPS helps you decide when to roll an existing covered call. Below is the
            recommended workflow.
          </p>
        </div>

        <div className="divide-y divide-[#27272A] text-[11px] leading-relaxed">
          <Step
            n={1}
            title="Enter your current covered call"
            body={
              <>
                In the <strong className="text-white">Position Tracker</strong> on the left, click
                <span className="font-mono text-[#007AFF]"> Add</span>, choose the ticker
                (or type a custom symbol like <span className="font-mono">QCOM</span>), pick the
                expiration and strike, then save.
              </>
            }
          />

          <Step
            n={2}
            title="Read the roll candidates table"
            body={
              <>
                The center table shows every call option you could roll into. Each cell is color-coded:{" "}
                <ColorTag c="cell-credit-strong" t="bright green" /> = strong roll,{" "}
                <ColorTag c="cell-credit-light" t="darker green" /> = good,{" "}
                <ColorTag c="cell-credit-marginal" t="yellow" /> = marginal,{" "}
                <ColorTag c="cell-debit" t="red" /> = you&rsquo;d pay (avoid). Use the date chips above the
                table to filter by expiration.
              </>
            }
          />

          <Step
            n={3}
            title="Click a candidate to evaluate"
            body={
              <>
                Click any row — the <strong className="text-white">Roll Recommendation</strong> card
                on the right shows the net credit, credit %, week-scaled score, breakeven and a
                verdict (Recommended / Acceptable / Marginal / Avoid).
              </>
            }
          />

          <Step
            n={4}
            title="Execute the roll in one click"
            body={
              <>
                Happy with the candidate? Use the buttons in the{" "}
                <strong className="text-white">Execute Roll</strong> section at the bottom of the
                Recommendation card:
                <br />
                <span className="font-mono text-[#00E676]">• Replace position</span> — closes the
                current covered call and opens the new one (most common).
                <br />
                <span className="font-mono text-[#A1A1AA]">• Keep + add new</span> — keeps the
                original in your tracker and adds the new leg alongside (useful for laddering).
                <br />
                Either choice is saved to your <strong className="text-white">Roll History</strong>{" "}
                so you can review every roll later.
              </>
            }
          />

          <Step
            n={5}
            title="Use the Heatmap Overview"
            body={
              <>
                The mini heatmap on the right gives a bird&rsquo;s-eye view of every (strike × expiration)
                cell. Click any colored cell to jump to that candidate in the table. The amber line
                marks where the underlying price sits.
              </>
            }
          />

          <Step
            n={6}
            title="How the scoring works"
            body={
              <>
                The model highlights candidates where the trade <strong className="text-white">collects a net
                credit</strong> AND the new strike is <strong className="text-white">at or above
                the current underlying price</strong> (out-of-the-money, lower assignment risk).
                Thresholds scale with how far out the new expiration sits — a 4-week roll needs ~4× the
                credit % of a 1-week roll to hit bright green. Whether the score fits your own
                strategy, risk tolerance, and tax situation is for you to decide.
              </>
            }
          />
        </div>

        <div className="px-4 py-2.5 border-t border-[#27272A] flex items-center justify-between">
          <Link
            to="/contact"
            data-testid="help-contact-link"
            className="flex items-center gap-1.5 text-[11px] text-[#00E676] hover:text-white transition-colors font-mono uppercase tracking-wider"
          >
            <Mail size={11} /> Contact us
          </Link>
          <span className="text-[10px] text-[#71717A]">Questions? Feedback?</span>
        </div>

        <div className="px-4 py-2.5 border-t border-[#27272A] text-[10px] text-[#71717A] leading-relaxed">
          Educational tool — not financial advice. See the full disclaimer at the bottom of the
          page.
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Step = ({ n, title, body }) => (
  <div className="px-4 py-3 flex gap-3">
    <div className="flex-shrink-0 w-5 h-5 border border-[#3F3F46] flex items-center justify-center font-mono text-[10px] text-[#00E676]">
      {n}
    </div>
    <div className="flex-1">
      <div className="text-xs font-semibold tracking-tight">{title}</div>
      <p className="mt-1 text-[#A1A1AA]">{body}</p>
    </div>
  </div>
);

const ColorTag = ({ c, t }) => (
  <span className="inline-flex items-center gap-1 mx-0.5">
    <span className={`${c} w-2.5 h-2.5 inline-block`} />
    <span className="text-white">{t}</span>
  </span>
);
